import MixpanelBrowser, {
  Config,
  RequestOptions,
  Callback,
} from 'mixpanel-browser';

import { SMap } from '../../helpers/types';
import { EventWithPayload, MixPanelEvent } from './events';
import auth, { isProduction } from '@client/components/auth';
import { getVersion } from 'releases';

export type MixpanelOptions = {
  name: string;
  options: Partial<Config>;
  token: string;
};

type EventProps = {
  eventName: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  properties?: SMap<any>;
  optionsOrCb?: RequestOptions | Callback;
  cb?: Callback;
  uid: string;
};

let _isInitialized = false;

export const initializeMixpanel = (config: {
  token: string;
  options: Partial<Config>;
}) => {
  if (!_isInitialized) _isInitialized = true;
  MixpanelBrowser.init(config.token, {
    debug: import.meta.env.VITE_ENV !== 'production',
    ...config.options,
  });
  console.info('Mixpanel initialized');
};

export const track = <T extends MixPanelEvent>(
  { type, payload }: T & { payload: { uid: string | number } },
  props?: Pick<EventProps, 'optionsOrCb' | 'cb'>,
) => {
  if (!_isInitialized) {
    // TODO: we may move those to .env, but that not a priority since it is not a secret
    const devToken = 'c4d78278547dfda40ab55e96d08f206f';
    const prodToken = 'd0cc7240d1640ac4b0cc271807bced6b';
    initializeMixpanel({
      token: isProduction() ? prodToken : devToken,
      options: { debug: !isProduction(), ignore_dnt: true },
    });

    if (type === 'Log In') {
      const { uid, ...p } = payload;
      MixpanelBrowser.people.set(p);
    }

    MixpanelBrowser.register({
      platform: 'web',
      'WebPage Version': getVersion(),
    });
  }

  if (payload.uid) MixpanelBrowser.identify(payload.uid + '');

  MixpanelBrowser.track(type, payload, props?.optionsOrCb, props?.cb);
};

type EventPayload<TName extends string, T> = T extends EventWithPayload<
  TName,
  infer P
>
  ? P
  : never;

type EventPayloadsMap = {
  [K in MixPanelEvent['type']]: EventPayload<K, MixPanelEvent>;
};

export type EventName = keyof EventPayloadsMap;

export const trackLogin = (
  payload: { uid: string } & Partial<EventPayloadsMap['Log In']>,
) => {
  const t: EventName = 'Log In';
  track({
    type: t,
    payload: payload as any,
  });
};

export const setUID = (uid: string) => {
  MixpanelBrowser.identify(uid);
};

export const mixTrack = <T extends EventName>(
  {
    type,
    payload,
  }: {
    type: T;
    payload: Omit<EventPayloadsMap[T], 'Influencer ID' | 'Influencer Name'>;
  },
  props?: Pick<EventProps, 'optionsOrCb' | 'cb'>,
) => {
  if (!auth.isInfluencer()) {
    return;
  }

  const user = auth.getUser();
  const modifiedPayload = {
    ...payload,
    uid: user.id,
    'Influencer ID': user?.influencer?.id,
    'Influencer Name':
      user.influencer?.firstName + ' ' + user.influencer?.lastName,
  };

  track({ type, payload: modifiedPayload } as any, props);
};
